import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Spinner,
    Button
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import { FaDownload, FaPrint } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ViewExam.css';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1;

function ViewStudentResult() {
    const token = localStorage.getItem("token");

    const { studentId, type, year } = useParams();
    const location = useLocation();
    const { className, typeName } = location.state || {};

    const [studentData, setStudentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [schoolInfo, setSchoolInfo] = useState({});

    useEffect(() => {
        fetchResults();
        fetchSchoolInfo();
    }, [studentId, type, year]);

    const fetchSchoolInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const data = response.data;

            if (data && data.length > 0) {
                setSchoolInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching school info:', error);
        }
    };

    const fetchResults = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${baseURL}/superadmin/results/student/${studentId}/type/${type}/year/${year}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            }
            );
            // console.log('Student data is ', response.data.data)
            setStudentData(response.data.data);
        } catch (error) {
            // console.error('Error fetching results:', error);
            toast.error('Error fetching results');
        } finally {
            setLoading(false);
        }
    };

    const generatePdfReport = () => {
        if (!studentData) return;

        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();

        // School Logo and Information (Header)
        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";

        pdf.addImage(img, 'PNG', 15, 15, 30, 30);

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College', 50, 22);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(schoolInfo.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.', 50, 28);
        pdf.text(`Tel: ${schoolInfo.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolInfo.schoolEmail || 'info@ummudhardha.lk'}`, 50, 34);

        // Report Title
        pdf.setFontSize(18);
        // Increased Y position to avoid overlap
        pdf.text(`Student Result - ${studentData.fullName} (${studentData.examType} ${year})`, pageWidth / 2, 55, { align: 'center' });

        let y = 65;
        pdf.setFontSize(14);
        pdf.text(`Class: ${className}`, 15, y);
        y += 10;
        pdf.text(`Name: ${studentData.fullName}`, 15, y);
        y += 10;
        pdf.text(`Registration Number: ${studentData.registrationNumber}`, 15, y);
        y += 10;
        pdf.text(`Exam Type: ${studentData.examType}`, 15, y);
        y += 10;
        pdf.text(`Year: ${studentData.year}`, 15, y);
        y += 10;
        pdf.text(`Total Marks: ${studentData.totalMarks}`, 15, y);
        y += 10;
        pdf.text(`Obtained Marks: ${studentData.obtainedMarks}`, 15, y);
        y += 10;
        pdf.text(`Rank: ${studentData.rank}`, 15, y);
        y += 10;
        pdf.text(`Percentage: ${studentData.overallPercentage}`, 15, y);
        y += 10;
        pdf.text(`Grade: ${studentData.overallGrade}`, 15, y);
        y += 10;

        pdf.setFontSize(14);
        pdf.text('Subject Results', 15, y);
        y += 10;

        const tableData = studentData.subjects.map(subject => [
            subject.subjectCode,
            subject.subjectName,
            subject.totalMarks,
            subject.obtainedMarks,
            subject.grade,
            subject.comments
        ]);

        pdf.autoTable({
            head: [['Subject Code', 'Subject Name', 'Total Marks', 'Obtained Marks', 'Grade', 'Comments']],
            body: tableData,
            startY: y,
            margin: { left: 15, right: 15 },
        });

        // Footer
        const totalPages = pdf.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);
            pdf.text(`Generated on: ${new Date().toLocaleDateString()} | ${schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College'}`,
                pageWidth - 40, pdf.internal.pageSize.getHeight() - 10);
        }

        pdf.save(`student-result-${studentData.fullName}-${studentData.examType}-${year}.pdf`);
    };

    const handlePrint = () => {
        if (!studentData) return;

        const tableStyle = `border-collapse: collapse; width: 100%; margin: 10px 0;`; // Added margin
        const thStyle = `background-color: #2980b9; color: white; padding: 8px; text-align: left; border: 1px solid #ddd;`;
        const tdStyle = `padding: 8px; text-align: left; border: 1px solid #ddd;`;

        const printContent = `
            <html>
            <head>
                <title>Student Result - ${studentData.fullName}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; } // Added margin to body
                    .header { text-align: center; margin-bottom: 20px; }
                    .school-name { font-size: 24px; font-weight: bold; }
                    .school-info { font-size: 14px; }
                    .result-title { font-size: 20px; margin-bottom: 10px; }
                    table { ${tableStyle} }
                    th { ${thStyle} }
                    td { ${tdStyle} }
                    .footer { text-align: center; margin-top: 20px; font-size: 12px; }
                    div { margin-bottom: 5px; } // Added space between text
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" alt="School Logo" width="100"/>
                    <div class="school-name">${schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
                    <div class="school-info">
                        ${schoolInfo.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.'}<br/>
                        Tel: ${schoolInfo.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolInfo.schoolEmail || 'info@ummudhardha.lk'}
                    </div>
                </div>
                <div class="result-title">Student Result - ${studentData.fullName} (${studentData.examType} ${year})</div>
                <div><strong>Class:</strong> ${className}</div>
                <div><strong>Name:</strong> ${studentData.fullName}</div>
                <div><strong>Registration Number:</strong> ${studentData.registrationNumber}</div>
                <div><strong>Exam Type:</strong> ${studentData.examType}</div>
                <div><strong>Year:</strong> ${studentData.year}</div>
                <div><strong>Total Marks:</strong> ${studentData.totalMarks}</div>
                <div><strong>Obtained Marks:</strong> ${studentData.obtainedMarks}</div>
                <div><strong>Rank:</strong> ${studentData.rank}</div>
                <div><strong>Percentage:</strong> ${studentData.overallPercentage}</div>
                <div><strong>Grade:</strong> ${studentData.overallGrade}</div>
                
                <h3>Subject Results</h3>
                <table border="1">
                    <thead>
                        <tr>
                            <th>Subject Code</th>
                            <th>Subject Name</th>
                            <th>Total Marks</th>
                            <th>Obtained Marks</th>
                            <th>Grade</th>
                            <th>Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${studentData.subjects.map(subject => `
                            <tr>
                                <td>${subject.subjectCode}</td>
                                <td>${subject.subjectName}</td>
                                <td>${subject.totalMarks}</td>
                                <td>${subject.obtainedMarks}</td>
                                <td>${subject.grade}</td>
                                <td>${subject.remarks}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="footer">Generated on: ${new Date().toLocaleDateString()} | ${schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    if (loading) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    if (!studentData) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <p>No result found for this student.</p>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Student Result</h3>
                                <div>
                                    <Button color="success" size="sm" onClick={generatePdfReport} style={{ marginRight: '10px' }}>
                                        <FaDownload /> Download PDF
                                    </Button>
                                    <Button color="info" size="sm" onClick={handlePrint}>
                                        <FaPrint /> Print
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <h4>Class: {className}</h4>
                                <h4>Name: {studentData.fullName}</h4>
                                <h4>Registration Number: {studentData.registrationNumber}</h4>
                                <h4>Exam Type: {studentData.examType}</h4>
                                <h4>Year: {studentData.year}</h4>
                                <h4>Total Marks: {studentData.totalMarks}</h4>
                                <h4>Obtained Marks: {studentData.obtainedMarks}</h4>
                                <h4>Rank: {studentData.rank}</h4>
                                <h4>Percentage: {studentData.overallPercentage}</h4>
                                <h4>Grade: {studentData.overallGrade}</h4>

                                <h5>Subject Results</h5>
                                <div className="table-responsive">
                                    <Table className="table">
                                        <thead>
                                            <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                                <th>Subject Code</th>
                                                <th>Subject Name</th>
                                                <th>Total Marks</th>
                                                <th>Obtained Marks</th>
                                                <th>Grade</th>
                                                <th>Percentage</th>
                                                <th>Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {studentData.subjects.map(subject => (
                                                <tr key={subject.subjectCode}>
                                                    <td>{subject.subjectCode}</td>
                                                    <td>{subject.subjectName}</td>
                                                    <td>{subject.totalMarks}</td>
                                                    <td>{subject.obtainedMarks}</td>
                                                    <td>{subject.grade}</td>
                                                    <td>{subject.percentage}</td>
                                                    <td>{subject.remarks}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default ViewStudentResult;
